import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { AppPathLocationStrategy } from './app.path-location-strategy';
import { InstagramWarningComponent } from './features/instagram-issue/instagram-warning.component';
import { InstagramIssueService } from './utils/instagram-issue.service';
import { ViewportDiagnosisService } from './utils/viewport-diagnosis.service';
import { NGXLogger } from 'ngx-logger';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, RouterOutlet, InstagramWarningComponent],
})
export class AppComponent implements OnInit {
  private platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private pathLocation = inject(AppPathLocationStrategy);
  private instagramIssueService = inject(InstagramIssueService);
  private viewportDiagnosisService = inject(ViewportDiagnosisService);
  private logger = inject(NGXLogger);

  readonly showInstagramWarning = signal(false);

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.checkInstagramIssue();
      this.checkAndApplyViewportFix();

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.handleDeferUiInteraction());
      } else {
        this.handleDeferUiInteraction();
      }

      const diagnosis = this.viewportDiagnosisService.runDiagnosis();

      if (diagnosis && (diagnosis.status !== 'ok' || diagnosis.viewport.safeAreaInsetBottom > 0)) {
        this.logger.warn('[AppComponent] Viewport diagnosis: ' + diagnosis.status, diagnosis);

        if (diagnosis.status === 'in-app-screen-bug') {
          this.showInstagramWarning.set(true);
        }
      }
    }
  }

  private async checkInstagramIssue(): Promise<void> {
    try {
      const hasInstagramIssue = await this.instagramIssueService.shouldShowInstagramWarning();
      if (hasInstagramIssue) {
        this.showInstagramWarning.set(true);
      }
    } catch (error) {
      console.error('Error checking Instagram issue:', error);
    }
  }

  private checkAndApplyViewportFix(): void {
    const ua = navigator.userAgent;
    const isInAppBrowser = /Instagram|FBAN|FBAV|FB_IAB/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);

    if (isInAppBrowser && isIOS) {
      setTimeout(() => {
        const screenHeight = window.screen.height;
        const innerHeight = window.innerHeight;
        const ratio = innerHeight / screenHeight;

        console.log('[Viewport Detection]', {
          screenHeight,
          innerHeight,
          ratio,
          needsFix: ratio > 0.98,
        });

        // Los navegadores in-app de Instagram/Facebook en iOS reportan mal el viewport.
        // Si el ratio innerHeight/screenHeight es muy alto (>0.98), indica que el navegador
        // está reportando casi toda la pantalla como disponible cuando en realidad
        // las barras de navegación ocupan espacio significativo.
        if (ratio > 0.98) {
          const adjustment = Math.round(screenHeight * 0.21); // 21% del screen height
          const appRoot = document.querySelector('app-root') as HTMLElement;

          if (appRoot) {
            appRoot.style.setProperty('--viewport-bottom-adjustment', `${adjustment}px`);
            appRoot.style.maxHeight = `calc(100vh - ${adjustment}px)`;
            console.log('[Viewport Fix] Fix aplicado:', `calc(100vh - ${adjustment}px)`);
          }
        } else {
          console.log('[Viewport Fix] No se necesita fix (ratio normal)');
        }
      }, 500);
    }
  }

  private handleDeferUiInteraction() {
    window['APP_IS_READY'] = true;

    if (window['APP_LAST_CLICKED_ELEMENT']) {
      if (window['APP_LAST_CLICKED_ELEMENT'].tagName === 'A') {
        const anchorUrl = window['APP_LAST_CLICKED_ELEMENT'].getAttribute('href');
        const anchorUrlWithoutSlug = anchorUrl.replace(this.pathLocation.getBaseHref(), '');

        this.router.navigate(['/' + anchorUrlWithoutSlug]);
      } else {
        window['APP_LAST_CLICKED_ELEMENT'].click();
      }

      setTimeout(() => {
        const overlay = document.querySelector('#appPrebootOverlay');
        if (overlay) {
          overlay.remove();
        }
      }, 500);
    }
    document.body.removeEventListener('click', window['APP_CLICK_CAPTURE_LISTENER']);
  }
}
